import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Music, Mic, GraduationCap, Sparkles, ArrowRight, Gift, Radio, Disc3, Heart, Star, Play } from 'lucide-react';
import { toast } from 'sonner';

const GAI_MUSIC_HERO = 'https://customer-assets.emergentagent.com/job_b45e9d54-c6e0-4deb-b583-023a1a99ea3a/artifacts/jv6f1b6h_Chief%20of%20Gangsta%20Dummies%20.png';
const GAI_MUSIC_ABOUT = 'https://customer-assets.emergentagent.com/job_b45e9d54-c6e0-4deb-b583-023a1a99ea3a/artifacts/zyy6zd9a_Gangsta%20AI%20for%20Dummies%20.png';
const HUMMINGBIRD = 'https://customer-assets.emergentagent.com/job_1b90f8c6-d70e-4858-8e6c-d4275accd79b/artifacts/l4bmy3e4_Official%20Hummingbird%20for%20TAV%20The%20Alchemy%20Village%20.png';

const SONG_TYPES = [
  { icon: Gift, label: 'Birthday Songs', desc: 'A custom song that celebrates the person you love — by name, by memory, by heart.', color: '#00d4ff' },
  { icon: Radio, label: 'Business Jingles', desc: 'A catchy, professional jingle that makes your brand unforgettable.', color: '#4da6ff' },
  { icon: Heart, label: 'Love Songs', desc: 'Say what words alone can\'t. A personalized love song for anniversaries, proposals, or just because.', color: '#7eb8ff' },
  { icon: Star, label: 'Motivation Anthems', desc: 'Your own personal anthem. The song that plays when you walk into a room and own it.', color: '#00e5ff' },
  { icon: Disc3, label: 'Memorial Tributes', desc: 'Honor someone you\'ve lost with a song that captures their spirit and keeps them alive in music.', color: '#4da6ff' },
  { icon: Mic, label: 'Custom Requests', desc: 'Got something else in mind? Tell Cabrini your vision and she\'ll make it real.', color: '#00d4ff' },
];

const LEARN_MODULES = [
  { num: '01', title: 'AI Music Basics', desc: 'How AI music generation actually works — no tech degree required.', color: '#00d4ff' },
  { num: '02', title: 'Finding Your Sound', desc: 'How to describe your vibe, genre, and mood so AI gives you exactly what you hear in your head.', color: '#4da6ff' },
  { num: '03', title: 'Lyrics + AI', desc: 'Write lyrics that hit — then let AI help you polish, structure, and refine them.', color: '#00e5ff' },
  { num: '04', title: 'Beats & Production', desc: 'Create beats, instrumentals, and full arrangements using AI tools — from lo-fi to gospel to trap.', color: '#7eb8ff' },
  { num: '05', title: 'Mixing & Finishing', desc: 'Take your raw AI creation and make it sound professional — mastering, mixing, final touches.', color: '#4da6ff' },
  { num: '06', title: 'Release & Share', desc: 'Get your music on Spotify, Apple Music, and social media. Cabrini shows you the path.', color: '#00d4ff' },
];

export default function MakeMusicAIPage() {
  const [loading, setLoading] = useState(false);

  const handleCheckout = async (packageId) => {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/gai-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="make-music-ai-page" className="mmai">
      {/* Animated grid background */}
      <div className="gai-grid-bg" />

      {/* Sub-nav */}
      <nav className="gai-subnav" data-testid="mmai-subnav">
        <span className="gai-subnav-logo">Make Music with AI</span>
        <div className="gai-subnav-links">
          <a href="#mmai-create">Get a Song</a>
          <a href="#mmai-learn">Learn to Create</a>
          <Link to="/music">Cabrini's Music</Link>
        </div>
      </nav>

      {/* HERO */}
      <div className="mmai-hero" data-testid="mmai-hero">
        <div className="mmai-hero-content">
          <p className="gai-hero-label">A Gangsta AI Experience</p>
          <h1 className="mmai-hero-title" data-testid="mmai-title">
            Make Music<br /><span className="gai-neon-text">with AI</span>
          </h1>
          <p className="mmai-hero-tagline">Your voice. Your story. AI-powered. Cabrini-delivered.</p>
          <p className="mmai-hero-desc">Whether you want a custom song created <em>for</em> you or want to learn how to create music yourself using AI — this is where the magic happens.</p>
          <div className="mmai-hero-cta">
            <a href="#mmai-create" className="gai-btn-primary" data-testid="mmai-order-btn">
              <Gift size={18} /> Order a Custom Song
            </a>
            <a href="#mmai-learn" className="gai-btn-secondary" data-testid="mmai-learn-btn">
              <GraduationCap size={18} /> Learn to Create
            </a>
          </div>
        </div>
        <div className="gai-hero-photo">
          <div className="gai-photo-glow" />
          <div className="gai-photo-ring gai-ring-1" />
          <div className="gai-photo-ring gai-ring-2" />
          <img src={GAI_MUSIC_HERO} alt="Cabrini — Music Producer + AI Teacher" className="gai-hero-img" data-testid="mmai-hero-img" />
          <span className="gai-hero-badge" data-testid="mmai-badge">Music Producer + AI Teacher</span>
        </div>
      </div>

      {/* Two paths intro */}
      <div className="mmai-paths-intro" data-testid="mmai-paths">
        <h2 className="gai-section-title">Two Ways to Make Music</h2>
        <div className="mmai-paths-grid">
          <a href="#mmai-create" className="mmai-path-card" data-testid="mmai-path-create">
            <div className="mmai-path-icon" style={{background: 'linear-gradient(135deg, #00d4ff, #4da6ff)'}}>
              <Mic size={32} />
            </div>
            <h3>I'll Make It For You</h3>
            <p>Tell Cabrini your story, your person, your moment — and she'll create a custom AI-powered song just for you.</p>
            <span className="mmai-path-link">See Song Types <ArrowRight size={14} /></span>
          </a>
          <a href="#mmai-learn" className="mmai-path-card" data-testid="mmai-path-learn">
            <div className="mmai-path-icon" style={{background: 'linear-gradient(135deg, #4da6ff, #7eb8ff)'}}>
              <GraduationCap size={32} />
            </div>
            <h3>Learn to Do It Yourself</h3>
            <p>Cabrini teaches you step-by-step how to use AI to create beats, lyrics, and full songs — even if you've never touched an instrument.</p>
            <span className="mmai-path-link">See the Course <ArrowRight size={14} /></span>
          </a>
        </div>
      </div>

      {/* ═══ SECTION 1: CUSTOM SONGS ═══ */}
      <div id="mmai-create" className="mmai-create" data-testid="mmai-create-section">
        <p className="gai-eyebrow">Let Cabrini Create For You</p>
        <h2 className="gai-section-title">Custom AI-Powered Songs</h2>
        <p className="gai-section-sub">Tell me the story. I'll turn it into music.</p>

        <div className="mmai-create-about">
          <img src={GAI_MUSIC_ABOUT} alt="Cabrini" className="mmai-create-img" />
          <div className="mmai-create-text">
            <p>"Music has always been how I express what words can't capture. Now, with AI, I can take YOUR story — your love, your loss, your celebration — and turn it into a real song. <em>Personalized. Professional. From my heart to yours.</em>"</p>
            <p className="mmai-create-sig">— Cabrini (Nonae' Michelle)</p>
          </div>
        </div>

        <div className="mmai-songs-grid">
          {SONG_TYPES.map((song, i) => {
            const Icon = song.icon;
            return (
              <div key={i} className="mmai-song-card" data-testid={`song-type-${i}`} style={{'--song-color': song.color}}>
                <div className="mmai-song-icon"><Icon size={24} /></div>
                <h3>{song.label}</h3>
                <p>{song.desc}</p>
              </div>
            );
          })}
        </div>

        <div className="mmai-create-pricing" data-testid="mmai-pricing-create">
          <div className="mmai-price-card">
            <h3>Custom Song Package</h3>
            <div className="gai-price-amt"><span className="gai-dollar">$</span><span className="gai-num">197</span></div>
            <ul>
              <li><Sparkles size={14} /> Full custom AI-produced song</li>
              <li><Sparkles size={14} /> Personalized lyrics based on your story</li>
              <li><Sparkles size={14} /> Professional mixing and mastering</li>
              <li><Sparkles size={14} /> Delivered as high-quality MP3 + WAV</li>
              <li><Sparkles size={14} /> 1 round of revisions included</li>
            </ul>
            <button className="gai-btn-primary gai-price-btn" data-testid="mmai-buy-song" disabled={loading} onClick={() => handleCheckout('custom-song')}>
              {loading ? 'Loading...' : 'Order My Custom Song'}
            </button>
          </div>
        </div>
      </div>

      {/* ═══ SECTION 2: LEARN TO CREATE ═══ */}
      <div id="mmai-learn" className="mmai-learn" data-testid="mmai-learn-section">
        <p className="gai-eyebrow">The DIY Path</p>
        <h2 className="gai-section-title">Learn to Make Music with AI</h2>
        <p className="gai-section-sub">6 modules. Zero instruments required. All heart.</p>

        <div className="mmai-learn-grid">
          {LEARN_MODULES.map((mod) => (
            <div key={mod.num} className="mmai-learn-card" data-testid={`learn-module-${mod.num}`} style={{'--mod-color': mod.color}}>
              <span className="mmai-learn-num">{mod.num}</span>
              <h3>{mod.title}</h3>
              <p>{mod.desc}</p>
            </div>
          ))}
        </div>

        <div className="mmai-learn-pricing" data-testid="mmai-pricing-learn">
          <div className="mmai-price-card mmai-price-card--featured">
            <span className="gai-best-badge">Part of Gangsta AI</span>
            <h3>Music Course Access</h3>
            <p className="mmai-included">Included with your Gangsta AI for Dummies subscription</p>
            <div className="gai-price-amt"><span className="gai-dollar">$</span><span className="gai-num">47</span><span className="gai-per">/month</span></div>
            <p className="mmai-or">or $397/year (save $167)</p>
            <button className="gai-btn-primary gai-price-btn" data-testid="mmai-enroll" disabled={loading} onClick={() => handleCheckout('gai-monthly')}>
              {loading ? 'Loading...' : 'Join Gangsta AI'}
            </button>
            <p className="gai-price-note">Get this + all 7 modules in the Gangsta AI curriculum</p>
          </div>
        </div>
      </div>

      {/* ═══ LISTEN SECTION ═══ */}
      <div className="mmai-listen" data-testid="mmai-listen">
        <img src={HUMMINGBIRD} alt="" className="mmai-listen-bird" />
        <h2 className="gai-section-title">Hear What Cabrini Creates</h2>
        <p className="gai-section-sub">Before AI, Cabrini was already making music. Now? The possibilities are limitless.</p>
        <Link to="/music" className="gai-btn-secondary mmai-listen-btn" data-testid="mmai-listen-link">
          <Play size={16} /> Listen to Cabrini's Music
        </Link>
      </div>

      {/* ═══ VILLAGE GATEWAY ═══ */}
      <div className="gai-gateway" data-testid="mmai-gateway">
        <img src={HUMMINGBIRD} alt="" className="gai-gateway-bird" />
        <h2 className="gai-section-title">Music Is Just One Path in <em>The Alchemy Village</em></h2>
        <p className="gai-gateway-text">The place where you don't just learn — you transform. Six paths. A mirror for your soul. And now, the power to create music that moves the world.</p>
        <Link to="/life-map" className="gai-btn-secondary gai-gateway-btn" data-testid="mmai-enter-village">
          Enter The Alchemy Village <ArrowRight size={16} />
        </Link>
      </div>

      {/* Footer */}
      <div className="gai-footer" data-testid="mmai-footer">
        <p className="gai-footer-domain">gangstaaifordummies.com</p>
        <p className="gai-footer-sub">Make Music with AI | A creation of The Alchemy Village</p>
      </div>
    </section>
  );
}
